import { conics, polygon as polygonOps } from '../geometry';
import { angle, EPSILON, vec } from '../math';
import { angleBetween, type Plane, planeThrough, signedDistance } from '../solid';

import { asCurve, asPoint, asVertices, type Evaluated, type MeasurementValue } from './evaluated';
import { type MeasureKind } from './source';

/**
 * Measurement evaluation.
 *
 * Measurements are ordinary DAG nodes, which is the whole reason they update by
 * themselves: a length measurement depends on its segment, so moving an endpoint dirties
 * the measurement exactly as it dirties any other dependent. No observer, no polling, no
 * separate refresh pass.
 *
 * Every measure returns `null` when its targets are the wrong shape or degenerate. A
 * measurement of a vanished construction disappears with it rather than freezing on a
 * stale number, which would be worse than showing nothing.
 */
export function measure(
  kind: MeasureKind,
  targets: readonly (Evaluated | null)[]
): MeasurementValue | null {
  switch (kind) {
    case 'length':
      return scalar(lengthOf(targets[0]), 'units');

    case 'distance': {
      /*
       * From a point to a plane, which is what a solid-geometry paper means nine times in ten:
       * "khoảng cách từ S đến (ABCD)". Measured in *space*, so it is the real distance and not
       * the foreshortened gap between two marks on the page.
       */
      const toPlane = distanceToPlane(targets);
      if (toPlane !== null) return scalar(toPlane, 'units');

      const a = asPoint(targets[0]);
      const b = asPoint(targets[1]);
      if (a === null || b === null) return null;
      return scalar(vec.distance(a, b), 'units');
    }

    case 'angle':
      return scalar(angleOf(targets), '°');

    case 'area':
      return scalar(areaOf(targets[0]), 'units²');

    case 'perimeter':
      return scalar(perimeterOf(targets[0]), 'units');

    case 'radius': {
      const value = targets[0];
      if (value == null) return null;
      if (value.kind === 'circle') return scalar(value.circle.radius, 'units');
      if (value.kind === 'arc') return scalar(value.arc.radius, 'units');
      return null;
    }

    case 'diameter': {
      const value = targets[0];
      if (value == null) return null;
      if (value.kind === 'circle') return scalar(value.circle.radius * 2, 'units');
      if (value.kind === 'arc') return scalar(value.arc.radius * 2, 'units');
      return null;
    }

    case 'coordinates': {
      const point = asPoint(targets[0]);
      return point === null ? null : { type: 'point', value: point };
    }

    case 'slope': {
      const line = asCurve(targets[0]);
      if (line === null) return null;
      const direction =
        line.kind === 'line'
          ? line.line.direction
          : line.kind === 'ray'
            ? line.ray.direction
            : line.kind === 'segment'
              ? vec.subtract(line.segment.b, line.segment.a)
              : null;
      if (direction === null || vec.isZero(direction)) return null;
      // A vertical line has undefined slope; report nothing rather than Infinity.
      if (Math.abs(direction.x) <= EPSILON) return null;
      return scalar(direction.y / direction.x, '');
    }

    case 'vector-components': {
      const value = targets[0];
      if (value == null) return null;
      if (value.kind === 'vector') {
        return { type: 'vector', value: vec.subtract(value.to, value.from) };
      }
      if (value.kind === 'segment') {
        return { type: 'vector', value: vec.subtract(value.segment.b, value.segment.a) };
      }
      return null;
    }
  }
}

/**
 * Which measurements a set of targets supports.
 *
 * Determined by *attempting* each one rather than by a table of rules like "a circle
 * affords radius, diameter, area". A table is a second statement of what `measure`
 * already decides, and the two drift the moment a measure learns a new shape — the panel
 * would then either offer something that yields nothing or hide something that works.
 * Trying is exact by construction, and the cost is a handful of arithmetic operations on
 * a selection the user just made by hand.
 */
export function applicableMeasures(targets: readonly (Evaluated | null)[]): readonly MeasureKind[] {
  return MEASURE_KINDS.filter((kind) => measure(kind, targets) !== null);
}

const MEASURE_KINDS: readonly MeasureKind[] = Object.freeze([
  'length',
  'distance',
  'angle',
  'area',
  'perimeter',
  'radius',
  'diameter',
  'coordinates',
  'slope',
  'vector-components',
] satisfies MeasureKind[]);

function scalar(value: number | null, unit: string): MeasurementValue | null {
  if (value === null || !Number.isFinite(value)) return null;
  return { type: 'scalar', value, unit };
}

function lengthOf(value: Evaluated | null | undefined): number | null {
  if (value == null) return null;
  switch (value.kind) {
    // A viewpoint: no geometry on the page at all.
    case 'space':
      return null;

    /*
     * Not measured. A plotted curve's length is an integral this kernel does not compute, and
     * a number derived from however many samples the plotter happened to take would change
     * when the sampling did — a measurement that moves on its own is worse than none.
     */
    case 'function-graph':
    case 'axes':
    case 'variation-table':
    case 'graph-marks':
      return null;

    case 'segment':
      return vec.distance(value.segment.a, value.segment.b);
    case 'vector':
      return vec.distance(value.from, value.to);
    case 'arc':
      return conics.arcLength(value.arc);
    case 'circle':
      return conics.circleCircumference(value.circle);
    case 'polyline':
      return polylineLength(value.points, value.closed);
    case 'polygon':
      return polygonOps.perimeter(value.vertices);
    // An infinite line has no length; a point, label or image has none either.
    case 'point':
    case 'ray':
    case 'line':
    case 'ellipse':
    case 'text':
    case 'measurement':
    case 'image':
    case 'solid':
    case 'angle-mark':
      // A projected wireframe has no length or area worth reporting: those belong to the
      // solid, and the drawing of it is a shadow rather than the thing.
      return null;
  }
}

/**
 * Angle measurement, in degrees.
 *
 * Two shapes are accepted, matching how a student would ask:
 * - three points `A`, `V`, `B` — the angle at vertex `V`, in `[0, 180]`;
 * - two linear entities — the angle *between the lines*, in `[0, 90]`, since a line has
 *   no orientation and reporting 170° for a pair meeting at 10° would be wrong.
 */
function angleOf(targets: readonly (Evaluated | null)[]): number | null {
  /*
   * Two planes, which is what "góc giữa (SBC) và (ABCD)" asks for.
   *
   * Taken first, because a plane's value is a polygon and would otherwise fall through to the
   * two-curve branch below and be measured *on the page* — the foreshortened angle between two
   * outlines, which is a real number and the wrong one. An angle in a projection is not the
   * angle in space, and this is the one measure that can now answer in space.
   */
  const between = dihedralOf(targets);
  if (between !== null) return between;

  const a = asPoint(targets[0]);
  const vertex = asPoint(targets[1]);
  const b = asPoint(targets[2]);

  if (a !== null && vertex !== null && b !== null) {
    const toA = vec.subtract(a, vertex);
    const toB = vec.subtract(b, vertex);
    if (vec.isZero(toA) || vec.isZero(toB)) return null;
    return angle.toDegrees(Math.abs(vec.angleBetween(toA, toB)));
  }

  const first = asCurve(targets[0]);
  const second = asCurve(targets[1]);
  const firstDirection = directionOf(first);
  const secondDirection = directionOf(second);
  if (firstDirection === null || secondDirection === null) return null;

  return angle.toDegrees(angle.betweenLines(vec.angle(firstDirection), vec.angle(secondDirection)));
}

/**
 * From a point to a plane, in space, or `null` when the targets are not one of each.
 *
 * Either order, because a reader writing `measure "distance" S p` and one writing
 * `measure "distance" p S` mean the same thing and neither is wrong.
 */
function distanceToPlane(targets: readonly (Evaluated | null)[]): number | null {
  for (const [first, second] of [
    [targets[0], targets[1]],
    [targets[1], targets[0]],
  ] as const) {
    const plane = planeOfRegion(second);
    if (plane === null) continue;
    if (first?.kind !== 'point' || first.in3d === undefined) continue;

    return Math.abs(signedDistance(plane, first.in3d));
  }
  return null;
}

/**
 * The angle between two planes, in degrees, or `null` when the targets are not two of them.
 *
 * The **acute** one: two planes make two supplementary angles and a paper always states the
 * smaller. Zero for parallel planes, which is the answer that makes "are these parallel?" a
 * question about a number rather than a second predicate that could disagree with the first.
 */
function dihedralOf(targets: readonly (Evaluated | null)[]): number | null {
  const a = planeOfRegion(targets[0]);
  const b = planeOfRegion(targets[1]);
  if (a === null || b === null) return null;

  return angle.toDegrees(angleBetween(a, b));
}

/** The plane a shaded region lies in, when its corners know where they are in space. */
function planeOfRegion(value: Evaluated | null | undefined): Plane | null {
  if (value?.kind !== 'polygon' || value.in3d === undefined) return null;

  const [first, second] = value.in3d;
  if (first === undefined || second === undefined) return null;

  for (const point of value.in3d.slice(2)) {
    const found = planeThrough(first, second, point);
    if (found !== null) return found;
  }
  return null;
}

function directionOf(curve: ReturnType<typeof asCurve>): ReturnType<typeof vec.vec2> | null {
  if (curve === null) return null;
  switch (curve.kind) {
    case 'line':
      return curve.line.direction;
    case 'ray':
      return curve.ray.direction;
    case 'segment': {
      const d = vec.subtract(curve.segment.b, curve.segment.a);
      return vec.isZero(d) ? null : d;
    }
    case 'circle':
    case 'arc':
    case 'ellipse':
      return null;
  }
}

function areaOf(value: Evaluated | null | undefined): number | null {
  if (value == null) return null;
  switch (value.kind) {
    // A viewpoint: no geometry on the page at all.
    case 'space':
      return null;

    // The area under a curve is an integral, and this is a drawing tool. Neither a frame of
    // reference nor a table of one encloses anything to measure.
    case 'function-graph':
    case 'axes':
    case 'variation-table':
    case 'graph-marks':
      return null;

    case 'polygon':
      return polygonOps.area(value.vertices);
    case 'polyline':
      return value.closed ? polygonOps.area(value.points) : null;
    case 'circle':
      return conics.circleArea(value.circle);
    case 'ellipse':
      return conics.ellipseArea(value.ellipse);
    case 'point':
    case 'segment':
    case 'ray':
    case 'line':
    case 'vector':
    case 'arc':
    case 'text':
    case 'measurement':
    case 'image':
    case 'solid':
    case 'angle-mark':
      // A projected wireframe has no length or area worth reporting: those belong to the
      // solid, and the drawing of it is a shadow rather than the thing.
      return null;
  }
}

function perimeterOf(value: Evaluated | null | undefined): number | null {
  if (value == null) return null;
  const vertices = asVertices(value);
  if (vertices !== null) return polygonOps.perimeter(vertices);
  if (value.kind === 'circle') return conics.circleCircumference(value.circle);
  if (value.kind === 'ellipse') return conics.ellipsePerimeter(value.ellipse);
  return null;
}

function polylineLength(points: readonly ReturnType<typeof vec.vec2>[], closed: boolean): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const previous = points[i - 1];
    const current = points[i];
    if (previous === undefined || current === undefined) continue;
    total += vec.distance(previous, current);
  }
  const first = points[0];
  const last = points[points.length - 1];
  if (closed && first !== undefined && last !== undefined) {
    total += vec.distance(last, first);
  }
  return total;
}

/** Formats a measurement for display. Kept beside the computation so units stay in step. */
export function formatMeasurement(value: MeasurementValue, precision = 2): string {
  switch (value.type) {
    case 'scalar': {
      const rounded = value.value.toFixed(precision);
      return value.unit === '' ? rounded : `${rounded} ${value.unit}`;
    }
    case 'point':
      return `(${value.value.x.toFixed(precision)}, ${value.value.y.toFixed(precision)})`;
    case 'vector':
      return `⟨${value.value.x.toFixed(precision)}, ${value.value.y.toFixed(precision)}⟩`;
  }
}
